'use client';

import { c as _c } from "react/compiler-runtime";
import { Radio } from '@common/components';
import { cn } from '@core/utils';
import type { ListGiftOrderHistoryDto } from '@generated/auth';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useEffect, useMemo, useState } from 'react';
import { AuthQueryOptions } from '@/api';
import { usePhotoReviewEvent } from '@/app/donation/_hooks/usePhotoReviewEvent/usePhotoReviewEvent';
import { ORDER_STATUS, ORDER_STATUS_LABEL } from '@/app/donation/constants';
import { PageNavigation } from '@/components/client';
import { ResponsiveDialog } from '@/components/organism/ResponsiveDialog';
import { WEBVIEW_TYPE_ENTRY } from '@/constants';
import { useWebviewTypeEntry } from '@/hooks/useWebviewTypeEntry';
import { useDeviceInfoStore } from '@/stores/DeviceStore';
import { GTM_EVENT_TYPE, trackGtmEvent } from '@/utils/analytics/gtm';
import { DesktopTitle } from '../_components/DesktopTitle';
import { EmptyState } from './_components/EmptyState';
import { Filter } from './_components/Filter';
import { OrderCard } from './_components/OrderCard';
import { RecommendPhotoReviewModal } from './_components/RecommendPhotoReviewModal';
const PERIOD_OPTIONS = [{
  value: 'today',
  label: '오늘'
}, {
  value: 'week',
  label: '1주일'
}, {
  value: 'month',
  label: '1개월'
}, {
  value: 'sixMonths',
  label: '6개월'
}, {
  value: 'year',
  label: '1년'
}];
const STATUS_OPTIONS = [{
  value: 'all',
  label: '전체주문'
}, {
  value: ORDER_STATUS.PAYMENT_COMPLETE,
  label: ORDER_STATUS_LABEL[ORDER_STATUS.PAYMENT_COMPLETE]
}, {
  value: ORDER_STATUS.DELIVERY_PREPARING,
  label: ORDER_STATUS_LABEL[ORDER_STATUS.DELIVERY_PREPARING]
}, {
  value: ORDER_STATUS.PURCHASE_CONFIRMED,
  label: ORDER_STATUS_LABEL[ORDER_STATUS.PURCHASE_CONFIRMED]
}, {
  value: ORDER_STATUS.EXCHANGE_AND_RETURN,
  label: ORDER_STATUS_LABEL[ORDER_STATUS.EXCHANGE_AND_RETURN]
}, {
  value: ORDER_STATUS.CANCELLED,
  label: ORDER_STATUS_LABEL[ORDER_STATUS.CANCELLED]
}];
const OrdersPage = () => {
  const $ = _c(71);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [selectedPeriod, setSelectedPeriod] = useState("month");
  const [isStatusDrawerOpen, setIsStatusDrawerOpen] = useState(false);
  const [isPeriodDrawerOpen, setIsPeriodDrawerOpen] = useState(false);
  const [isRecommendPhotoReviewModalOpen, setIsRecommendPhotoReviewModalOpen] = useState(false);
  const {
    entry
  } = useWebviewTypeEntry();
  const {
    isEventProcess
  } = usePhotoReviewEvent();
  const webviewType = useDeviceInfoStore(_temp);
  let endDate;
  let t0;
  if ($[0] !== selectedPeriod) {
    const today = dayjs();
    endDate = today.format("YYYY-MM-DD");
    t0 = {
      today: today.format("YYYY-MM-DD"),
      week: today.subtract(7, "day").format("YYYY-MM-DD"),
      month: today.subtract(1, "month").format("YYYY-MM-DD"),
      sixMonths: today.subtract(6, "month").format("YYYY-MM-DD"),
      year: today.subtract(1, "year").format("YYYY-MM-DD")
    }[selectedPeriod] || today.subtract(1, "month").format("YYYY-MM-DD");
    $[0] = selectedPeriod;
    $[1] = endDate;
    $[2] = t0;
  } else {
    endDate = $[1];
    t0 = $[2];
  }
  const startDate = t0;
  let t1;
  if ($[3] !== endDate || $[4] !== startDate) {
    t1 = {
      startSearchDate: startDate,
      endSearchDate: endDate,
      searchOrderStatus: undefined
    };
    $[3] = endDate;
    $[4] = startDate;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const {
    startSearchDate,
    endSearchDate,
    searchOrderStatus
  } = t1;
  let t2;
  if ($[6] !== endSearchDate || $[7] !== searchOrderStatus || $[8] !== startSearchDate) {
    t2 = AuthQueryOptions["get:/wello/v2/auth/home/gift/order"]({
      query: {
        page: 1,
        size: 100,
        endSearchDate,
        searchOrderStatus,
        startSearchDate
      }
    });
    $[6] = endSearchDate;
    $[7] = searchOrderStatus;
    $[8] = startSearchDate;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== t2) {
    t3 = {
      ...t2,
      select: _temp2
    };
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  const {
    data: apiData
  } = useQuery(t3);
  let t4;
  if ($[12] !== entry || $[13] !== isEventProcess) {
    t4 = () => {
      if (isEventProcess && !getIsRecommendPhotoReviewEventModalOpened() && entry === WEBVIEW_TYPE_ENTRY.WELLO) {
        setIsRecommendPhotoReviewModalOpen(true);
        setIsRecommendPhotoReviewEventModalOpened();
      }
    };
    $[12] = entry;
    $[13] = isEventProcess;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== entry || $[16] !== isEventProcess || $[17] !== webviewType) {
    t5 = [isEventProcess, webviewType, entry];
    $[15] = entry;
    $[16] = isEventProcess;
    $[17] = webviewType;
    $[18] = t5;
  } else {
    t5 = $[18];
  }
  useEffect(t4, t5);
  let t6;
  if ($[19] !== apiData?.contents) {
    t6 = apiData?.contents || [];
    $[19] = apiData?.contents;
    $[20] = t6;
  } else {
    t6 = $[20];
  }
  const allOrders = t6;
  let t7;
  if ($[21] !== allOrders || $[22] !== selectedStatus) {
    t7 = selectedStatus === "all" ? allOrders : allOrders.filter(order => {
      const normalizedStatus = order.orderStatusCd?.replace("GIFT_ORDER_STATUS_", "");
      return normalizedStatus === selectedStatus;
    });
    $[21] = allOrders;
    $[22] = selectedStatus;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const orders = t7;
  const isEmpty = orders.length === 0;
  let t8;
  if ($[24] !== selectedStatus) {
    t8 = STATUS_OPTIONS.find(option => option.value === selectedStatus)?.label || "\uC804\uCCB4\uC8FC\uBB38";
    $[24] = selectedStatus;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  const statusLabel = t8;
  let t9;
  if ($[26] !== selectedPeriod) {
    t9 = PERIOD_OPTIONS.find(option_0 => option_0.value === selectedPeriod)?.label || "1\uAC1C\uC6D4";
    $[26] = selectedPeriod;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  const periodLabel = t9;
  const handleProductClick = _temp3;
  let t10;
  let t11;
  let t12;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <PageNavigation title={"\uC8FC\uBB38\uB0B4\uC5ED"} isNotUsedHomeButton={true} donationHomeButton={true} />;
    t11 = <DesktopTitle className="mb-20">주문내역</DesktopTitle>;
    t12 = cn("mb-16 flex items-center justify-end gap-12 px-16", "mobile-tablet:px-20");
    $[28] = t10;
    $[29] = t11;
    $[30] = t12;
  } else {
    t10 = $[28];
    t11 = $[29];
    t12 = $[30];
  }
  let t13;
  if ($[31] !== orders.length) {
    t13 = <p className="mr-auto font-500 text-Gray-900 typo-body4">전체 {orders.length}개</p>;
    $[31] = orders.length;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => setIsStatusDrawerOpen(true);
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== statusLabel) {
    t15 = <Filter label={statusLabel} onClick={t14} />;
    $[34] = statusLabel;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = () => setIsPeriodDrawerOpen(true);
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  let t17;
  if ($[37] !== periodLabel) {
    t17 = <Filter label={periodLabel} onClick={t16} />;
    $[37] = periodLabel;
    $[38] = t17;
  } else {
    t17 = $[38];
  }
  let t18;
  if ($[39] !== t13 || $[40] !== t15 || $[41] !== t17) {
    t18 = <section className={t12}>{t13}{t15}{t17}</section>;
    $[39] = t13;
    $[40] = t15;
    $[41] = t17;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  let t19;
  if ($[43] !== isEmpty || $[44] !== orders) {
    t19 = isEmpty ? <EmptyState /> : <section className="flex flex-col gap-16 pb-20"><ul className={cn("flex flex-col gap-42 px-0", "mobile-tablet:px-16")}>{orders.map(order_1 => <li key={order_1.giftOrderId}><OrderCard order={order_1} onProductClick={() => handleProductClick(order_1)} /></li>)}</ul></section>;
    $[43] = isEmpty;
    $[44] = orders;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = () => setIsStatusDrawerOpen(false);
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = value => {
      setSelectedStatus(value);
      setIsStatusDrawerOpen(false);
    };
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = STATUS_OPTIONS.map(_temp4);
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== selectedStatus) {
    t23 = <Radio.Group value={selectedStatus} onChange={t21} theme="black" className="grid grid-cols-2 gap-8">{t22}</Radio.Group>;
    $[49] = selectedStatus;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== isStatusDrawerOpen || $[52] !== t23) {
    t24 = <ResponsiveDialog opened={isStatusDrawerOpen} onClose={t20} title={"\uC8FC\uBB38\uC0C1\uD0DC \uC120\uD0DD"} drawerType="bottom">{t23}</ResponsiveDialog>;
    $[51] = isStatusDrawerOpen;
    $[52] = t23;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = () => setIsPeriodDrawerOpen(false);
    $[54] = t25;
  } else {
    t25 = $[54];
  }
  let t26;
  if ($[55] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = value_0 => {
      setSelectedPeriod(value_0);
      setIsPeriodDrawerOpen(false);
    };
    $[55] = t26;
  } else {
    t26 = $[55];
  }
  let t27;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = PERIOD_OPTIONS.map(_temp5);
    $[56] = t27;
  } else {
    t27 = $[56];
  }
  let t28;
  if ($[57] !== selectedPeriod) {
    t28 = <Radio.Group value={selectedPeriod} onChange={t26} theme="black" className="flex flex-col items-start gap-8">{t27}</Radio.Group>;
    $[57] = selectedPeriod;
    $[58] = t28;
  } else {
    t28 = $[58];
  }
  let t29;
  if ($[59] !== isPeriodDrawerOpen || $[60] !== t28) {
    t29 = <ResponsiveDialog opened={isPeriodDrawerOpen} onClose={t25} title={"\uC8FC\uBB38\uAE30\uD55C \uC120\uD0DD"} drawerType="bottom">{t28}</ResponsiveDialog>;
    $[59] = isPeriodDrawerOpen;
    $[60] = t28;
    $[61] = t29;
  } else {
    t29 = $[61];
  }
  let t30;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = () => setIsRecommendPhotoReviewModalOpen(false);
    $[62] = t30;
  } else {
    t30 = $[62];
  }
  let t31;
  if ($[63] !== isRecommendPhotoReviewModalOpen) {
    t31 = <RecommendPhotoReviewModal open={isRecommendPhotoReviewModalOpen} onClose={t30} />;
    $[63] = isRecommendPhotoReviewModalOpen;
    $[64] = t31;
  } else {
    t31 = $[64];
  }
  let t32;
  if ($[65] !== t18 || $[66] !== t19 || $[67] !== t24 || $[68] !== t29 || $[69] !== t31) {
    t32 = <section>{t10}{t11}{t18}{t19}{t24}{t29}{t31}</section>;
    $[65] = t18;
    $[66] = t19;
    $[67] = t24;
    $[68] = t29;
    $[69] = t31;
    $[70] = t32;
  } else {
    t32 = $[70];
  }
  return t32;
};
export default OrdersPage;
const SESSION_STORAGE_KEY = {
  /** 포토후기 이벤트 관련 모달 열었는지 여부 */
  RECOMMEND_PHOTO_REVIEW_EVENT_MODAL_OPENED: {
    key: 'RECOMMEND_PHOTO_REVIEW_EVENT_MODAL_OPENED',
    activeValue: 'true'
  } as const
} as const;
const getIsRecommendPhotoReviewEventModalOpened = () => {
  return sessionStorage.getItem(SESSION_STORAGE_KEY.RECOMMEND_PHOTO_REVIEW_EVENT_MODAL_OPENED.key) === SESSION_STORAGE_KEY.RECOMMEND_PHOTO_REVIEW_EVENT_MODAL_OPENED.activeValue;
};
const setIsRecommendPhotoReviewEventModalOpened = () => {
  sessionStorage.setItem(SESSION_STORAGE_KEY.RECOMMEND_PHOTO_REVIEW_EVENT_MODAL_OPENED.key, SESSION_STORAGE_KEY.RECOMMEND_PHOTO_REVIEW_EVENT_MODAL_OPENED.activeValue);
};
function _temp(state) {
  return state.webviewType;
}
function _temp2(t0) {
  const {
    context
  } = t0;
  return context;
}
function _temp3(order_0) {
  const firstProduct = order_0.products?.[0];
  trackGtmEvent(GTM_EVENT_TYPE.SELECT_ITEM, {
    currency: "KRW",
    value: 0,
    local_name: order_0.homeInstitutionName,
    items: [{
      item_type: "\uB2F5\uB840\uD488",
      item_list_name: "\uC8FC\uBB38\uB0B4\uC5ED",
      item_list_id: order_0.giftOrderId,
      item_name: firstProduct?.productName ?? "",
      quantity: firstProduct?.quantity ?? 1,
      item_id: firstProduct?.productNumber ?? "",
      price: 0
    }]
  });
}
function _temp4(option_1) {
  return <Radio.Item key={option_1.value} value={option_1.value} className="w-full justify-start py-8">{option_1.label}</Radio.Item>;
}
function _temp5(option_2) {
  return <Radio.Item key={option_2.value} value={option_2.value} className="py-4">{option_2.label}</Radio.Item>;
}